import moment, {Moment} from 'moment';
import {observable, transaction} from 'mobx';
import configString from '../utility/Config';
import {HistoryHandler, HistoryItem} from './MemoryBarStore';
import {DateUtils} from '../utility/DateUtils';
import * as TableAPI from '../apis/TableAPI';
import {toast} from 'react-toastify';

export interface DateRange {
    startDate?: Moment;
    endDate?: Moment;
}

export interface TimeState {
    mainDateRange: DateRange;
    compareDateRange: DateRange;
    allowDateCompareRange: boolean;
    setMainDateRange: (mainDateRange: DateRange) => void;
    setMainDateRangeWithoutReload: (mainDateRange: DateRange) => void;
    setAllowDateCompareRange: (allowDateCompareRange: boolean) => void;
    isMainDateRangeAvailable: () => boolean;
    setDefaultMainDateRange: () => Promise<{mainDateRange: DateRange; compareDateRange: DateRange}>;
    setCompareDateRange: (compareDateRange: DateRange) => void;
    setCompareDateRangeWithoutReload: (compareDateRange: DateRange) => void;
    setAllowDateCompareRangeWithoutReload: (allowDateCompareRange: boolean) => void;
}

class TimeIntervalStore implements HistoryHandler, TimeState {
    constructor(
        private onDateRangeChange: (forceTriggerChannelChange?: boolean) => void,
        private urlDateRange: {interval: string},
        private setUrlDateRange: (dateRange: DateRange) => void
    ) {}
    @observable public mainDateRange: DateRange = {};
    @observable public compareDateRange: DateRange = {};
    @observable public allowDateCompareRange = false;

    public applyHistory = (history: HistoryItem): void => {
        this.allowDateCompareRange = history.allowDateCompareRange;
        if (history.mainDateRange) {
            this.setMainDateRangeWithoutReload(history.mainDateRange);
        }
        if (history.compareDateRange) {
            this.setCompareDateRangeWithoutReload(history.compareDateRange);
        }
    };

    public getPartialHistory = (): Partial<HistoryItem> => ({
        allowDateCompareRange: this.allowDateCompareRange,
        mainDateRange: DateUtils.copyDateRange(this.mainDateRange),
        compareDateRange: DateUtils.copyDateRange(this.compareDateRange),
    });

    public isMainDateRangeAvailable() {
        return this.mainDateRange.startDate !== undefined;
    }

    private getDateIntervalFromUrl = (): DateRange => {
        const dates = this.urlDateRange.interval
            .split('/')
            .map((dateString) => moment(dateString))
            .filter((date) => date.isValid());
        return dates.length === 2 ? {startDate: dates[0], endDate: dates[1]} : {};
    };

    public setDefaultMainDateRange(): Promise<{mainDateRange: DateRange; compareDateRange: DateRange}> {
        const urlDateInterval = this.getDateIntervalFromUrl();

        if (DateUtils.isDateRange(urlDateInterval)) {
            return this.setDefaultMainDateRangeHelper(urlDateInterval);
        }

        let startDateEnv = moment(configString('ANALYTICS_START_DATE', ''));
        let endDateEnv = moment(configString('ANALYTICS_END_DATE', ''));
        if (startDateEnv.isValid()) {
            if (!endDateEnv.isValid()) {
                endDateEnv = startDateEnv.clone().add(6, 'days');
            }
        } else if (endDateEnv.isValid()) {
            startDateEnv = endDateEnv.clone().subtract(6, 'days');
        }

        if (startDateEnv.isValid() && endDateEnv.isValid()) {
            return this.setDefaultMainDateRangeHelper({startDate: startDateEnv, endDate: endDateEnv});
        }

        return TableAPI.lastActivityTimestamp()
            .then((resp) => {
                if (resp.data === 0) {
                    toast.error('Keine Serverdaten verfügbar');
                    throw Error('Server returned lastActivity timestamp of 0');
                }

                const endDate = moment(resp.data, 'x');
                const startDate = moment(endDate, 'x').subtract(6, 'days');
                return this.setDefaultMainDateRangeHelper({startDate, endDate});
            })
            .catch(() => {
                console.log('failed to fetch timestamp from server');
                const endDate = moment();
                const startDate = moment(endDate).subtract(6, 'days');
                return this.setDefaultMainDateRangeHelper({startDate, endDate});
            });
    }

    private setDefaultMainDateRangeHelper(mainDateRange: DateRange) {
        this.setMainDateRange(mainDateRange);
        return Promise.resolve({mainDateRange: this.mainDateRange, compareDateRange: this.compareDateRange});
    }

    /**
     * Sets activeDateCompare to a date interval the same size as the currently selected main date range
     * directly before the main date range
     */
    public setMainDateRange = (mainDateRange: DateRange): void => {
        this.setMainDateRangeWithoutReload(mainDateRange);
        this.onDateRangeChange();
    };

    public setMainDateRangeWithoutReload = (mainDateRange: DateRange): void => {
        transaction(() => {
            const {startDate, endDate} = mainDateRange;
            if (startDate === undefined || endDate === undefined) {
                this.mainDateRange = {};
                this.compareDateRange = {};
                return;
            }

            this.mainDateRange = {
                startDate: startDate ? startDate.startOf('day') : undefined,
                endDate: endDate.endOf('day'),
            };
            const duration = endDate.diff(startDate, 'days');
            const endDateCompare = moment(startDate).subtract(1, 'days');
            this.compareDateRange = {
                startDate: moment(endDateCompare)
                    .subtract(duration, 'days')
                    .startOf('day'),
                endDate: moment(endDateCompare).endOf('day'),
            };
            this.setUrlDateRange(this.mainDateRange);
        });
    };

    public setCompareDateRangeWithoutReload = (compareDateRange: DateRange): void => {
        const {startDate, endDate} = compareDateRange;
        this.compareDateRange = {
            startDate: startDate ? startDate.startOf('day') : undefined,
            endDate: endDate ? endDate.endOf('day') : undefined,
        };
    };

    public setCompareDateRange = (compareDateRange: DateRange): void => {
        this.setCompareDateRangeWithoutReload(compareDateRange);
        this.onDateRangeChange();
    };

    public setAllowDateCompareRangeWithoutReload = (allowDateCompareRange: boolean): void => {
        this.allowDateCompareRange = allowDateCompareRange;
    };

    public setAllowDateCompareRange = (allowDateCompareRange: boolean): void => {
        this.allowDateCompareRange = allowDateCompareRange;
        this.onDateRangeChange();
    };
}

export default TimeIntervalStore;



// WEBPACK FOOTER //
// ./src/stores/TimeIntervalStore.ts